/*
 * HomeFixer360 - Privacy Policy
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React from 'react';
import { ArrowLeft, Shield, Lock, Eye, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-600">How we collect, use, and protect your information</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          
          {/* Last Updated */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Last Updated: January 1, 2025</span>
            </div>
            <p className="text-blue-700 mt-2">This Privacy Policy is effective immediately and applies to all users of HomeFixer360.</p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-6 h-6 text-blue-600 mr-3" />
              Our Commitment to Your Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At Idea Guy Brands Incorporated ("we," "us," or "our"), we are committed to protecting the privacy and security 
              of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use HomeFixer360, our satellite roof measurement platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Contact Information:</strong><br />
              Guy T. Dunn Sr.<br />
              Idea Guy Brands Incorporated<br />
              Email: guy@ideaguybrands.com
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, company name, business license information</li>
              <li><strong>Billing Information:</strong> Payment card details, billing address, tax identification numbers</li>
              <li><strong>Profile Information:</strong> Custom pricing rates, regional settings, company branding preferences</li>
              <li><strong>Communication Data:</strong> Email correspondence, support tickets, feedback, and survey responses</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Property and Measurement Data</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Property Addresses:</strong> Street addresses, GPS coordinates for satellite measurement requests</li>
              <li><strong>Satellite Imagery:</strong> Roof measurements, area calculations, complexity factors</li>
              <li><strong>Estimates and Proposals:</strong> Generated quotes, material lists, labor calculations</li>
              <li><strong>Customer Data:</strong> Customer names, contact information, proposal acceptance records</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Technical Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Usage Data:</strong> Platform usage statistics, feature utilization, session duration</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Log Data:</strong> Access logs, error reports, performance metrics</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Service Delivery:</strong> Provide satellite roof measurements, generate estimates and proposals</li>
              <li><strong>Account Management:</strong> Create and maintain your account, process payments, provide customer support</li>
              <li><strong>Platform Improvement:</strong> Analyze usage patterns to enhance features and user experience</li>
              <li><strong>Communication:</strong> Send service updates, billing information, and important notifications</li>
              <li><strong>Legal Compliance:</strong> Meet regulatory requirements, respond to legal requests, protect our rights</li>
              <li><strong>Business Operations:</strong> Fraud prevention, security monitoring, backup and disaster recovery</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties, except:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Service Providers:</strong> Trusted partners who assist in platform operations (payment processing, cloud hosting)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfer:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Consent:</strong> When you explicitly authorize sharing for specific purposes</li>
              <li><strong>Emergency Situations:</strong> To protect the safety of users or prevent illegal activity</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-5 h-5 text-green-600 mr-2" />
              4. Data Security and Protection
            </h2>
            <p className="text-gray-700 mb-4">We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Encryption:</strong> All data transmission uses SSL/TLS encryption (256-bit)</li>
              <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication</li>
              <li><strong>Data Centers:</strong> SOC 2 Type II certified cloud infrastructure</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
              <li><strong>Backups:</strong> Regular encrypted backups with geographic distribution</li>
              <li><strong>Employee Training:</strong> Regular security awareness and privacy training</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-5 h-5 text-purple-600 mr-2" />
              5. Your Privacy Rights
            </h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request copies of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-700 mt-4">
              <strong>To exercise these rights, contact us at:</strong> guy@ideaguybrands.com
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Account Data:</strong> Retained while your account is active and for 7 years after closure for legal compliance</li>
              <li><strong>Measurement Data:</strong> Retained for 10 years to support warranty claims and legal requirements</li>
              <li><strong>Payment Data:</strong> Retained for 7 years for accounting and tax purposes</li>
              <li><strong>Technical Logs:</strong> Retained for 90 days for security and performance monitoring</li>
            </ul>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Maintain your login session and platform preferences</li>
              <li>Analyze platform usage and improve user experience</li>
              <li>Prevent fraud and enhance security</li>
              <li>Provide customer support and troubleshooting</li>
            </ul>
            <p className="text-gray-700 mt-4">
              For detailed information about our cookie usage, please see our <a href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</a>.
            </p>
          </section>

          {/* International Transfers */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700">
              Your information may be transferred to and processed in countries other than your country of residence. 
              We ensure appropriate safeguards are in place, including standard contractual clauses and adequacy decisions, 
              to protect your information during international transfers.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700">
              HomeFixer360 is intended for business use by home service pros and is not designed for individuals under 18. 
              We do not knowingly collect personal information from children under 18. If you believe we have collected 
              information from a child, please contact us immediately.
            </p>
          </section>

          {/* Updates to Policy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Updates to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
              We will notify you of material changes by email and by posting the updated policy on our platform. 
              Your continued use of HomeFixer360 after changes take effect constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us About Privacy</h2>
            <p className="text-gray-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Privacy Officer:</strong> Guy T. Dunn Sr.</p>
              <p><strong>Company:</strong> Idea Guy Brands Incorporated</p>
              <p><strong>Email:</strong> guy@ideaguybrands.com</p>
              <p><strong>Subject Line:</strong> "Privacy Policy Inquiry - HomeFixer360"</p>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              We will respond to your privacy-related inquiries within 30 days of receipt.
            </p>
          </section>

          {/* Copyright Footer */}
          <div className="border-t pt-6 mt-8 text-center text-gray-500 text-sm">
            <p>© 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated. All Rights Reserved.</p>
            <p>HomeFixer360™ is a trademark of Idea Guy Brands Incorporated.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;